import { useRef, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '@vd/ui';
import type { TourDefinition } from './tours';
import { modalOpen } from './modalOpen';
import styles from './TourOfferCard.module.css';

interface TourOfferCardProps {
  tour: TourDefinition;
  /** Starts the tour, whose first step takes the card's place: nothing shrinks. */
  onStart(): void;
  /** Turns it down, for good: the card shrinks into Get started. */
  onDecline(card: HTMLElement | null): void;
}

/**
 * "New here?" (TOADO-981): a tour offered once, in the corner, to someone
 * who has never started or declined it. Nothing covers the page and focus
 * stays where it was: the person is in the middle of something, and the
 * card waits beside it. Escape from inside it is No thanks.
 */
export function TourOfferCard({ tour, onStart, onDecline }: TourOfferCardProps) {
  const cardRef = useRef<HTMLElement>(null);
  const titleId = `tour-offer-${tour.id}-title`;
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || modalOpen()) return;
    event.preventDefault();
    event.stopPropagation();
    onDecline(cardRef.current);
  };
  return createPortal(
    <section
      ref={cardRef}
      className={styles.card}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      onKeyDown={onKeyDown}
      data-tour-offer={tour.id}
    >
      <div className={styles.head}>
        <h2 id={titleId} className={styles.title}>New here?</h2>
        <Button size="sm" variant="ghost" iconOnly leftIcon={<X size={14} aria-hidden />} aria-label="Close and skip the tour" title="No thanks" onClick={() => onDecline(cardRef.current)} />
      </div>
      <p className={styles.body}>{tour.offer?.body}</p>
      <div className={styles.actions}>
        <Button size="sm" variant="primary" onClick={onStart}>Take the tour</Button>
        <Button size="sm" variant="ghost" onClick={() => onDecline(cardRef.current)}>No thanks</Button>
      </div>
    </section>,
    document.body,
  );
}
